import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { X, Truck, Check, Clock, Flag, Loader2, MapPin, ChevronDown } from "lucide-react";
import { isHaster, openHours, daysLate, containerNo, capacityShort } from "../lib/tourHelpers";

/**
 * Hængerkørsel (2026-10-20). Vises når en chauffør med hænger starter en
 * containertur: flere containere fra samme plads kan komme med (ikke
 * obligatorisk). Samme materiale står øverst, derefter andre materialer.
 * 2026-10-22: Ture fra tidligere dage står under deres materiale (ikke i en
 * egen gruppe) med et "for sent"-mærke - materialet er det man vælger efter.
 * I hver gruppe: egne ture, haster, forsinkede (ældste først), tidsrum.
 * Både chaufførens egne og ledige ture vises.
 * addMode: turen kører allerede - vælg den næste til hængeren.
 * 2026-10-25: Containere fra ANDRE pladser står nederst under "Andre pladser"
 * (lukket, til man åbner den). Serveren ved, at de er hentet et andet sted
 * end bilens container, og prisen følger firmaets indstilling (Økonomi ›
 * Indstillinger: standard fuld pris for hver). Chaufføren tager ikke stilling
 * til pris - det er den samme situation, uanset hvem der kører.
 */
export default function DriverRunSheet({ anchor, basePlads = "", active = [], candidates = [], slots = 0, trailerName = "", addMode = false, preselect = [], onConfirm, onClose }) {
  // 2026-10-25: En planlagt hængerkørsel: planlæggerens ture er valgt på forhånd.
  const [sel, setSel] = useState(() => preselect.filter(id => candidates.some(c => c.id === id)).slice(0, slots));
  const [busy, setBusy] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const order = (a, b) => (a._mine === b._mine ? 0 : a._mine ? -1 : 1) || (isHaster(a) === isHaster(b) ? 0 : isHaster(a) ? -1 : 1)
    || String(a.date || "").slice(0, 10).localeCompare(String(b.date || "").slice(0, 10))
    || String(openHours(a).to || "99").padStart(5, "0").localeCompare(String(openHours(b).to || "99").padStart(5, "0"));
  const groups = useMemo(() => {
    const mat = (anchor?.fraction || "").trim().toLowerCase();
    const here = candidates.filter(t => !t._other_place);
    const same = here.filter(t => (t.fraction || "").trim().toLowerCase() === mat).sort(order);
    const other = here.filter(t => (t.fraction || "").trim().toLowerCase() !== mat).sort(order);
    return [
      { key: "same", title: `Samme materiale${anchor?.fraction ? ` (${anchor.fraction})` : ""}`, list: same },
      { key: "other", title: "Andre materialer", list: other },
    ].filter(g => g.list.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor, candidates]);
  // Andre pladser: én gruppe pr. plads (alfabetisk), haster og forsinkede først.
  const elsewhere = useMemo(() => {
    const m = new Map();
    candidates.filter(t => t._other_place).forEach(t => {
      const k = t.plads || "Uden plads";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(t);
    });
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0], "da")).map(([k, list]) => ({ key: k, list: list.sort(order) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates]);
  const elsewhereCount = elsewhere.reduce((n, g) => n + g.list.length, 0);
  const pickedElsewhere = candidates.filter(t => t._other_place && sel.includes(t.id));
  if (!anchor) return null;
  const toggle = (id) => setSel(s => (s.includes(id) ? s.filter(x => x !== id) : s.length >= slots ? s : [...s, id]));
  const confirm = async () => {
    setBusy(true);
    try { await onConfirm(sel); } finally { setBusy(false); }
  };
  const others = active.filter(t => t.id !== anchor.id);
  const label = addMode
    ? (sel.length ? `Læg ${sel.length === 1 ? "turen" : `${sel.length} ture`} på hængeren` : "Vælg en tur")
    : (sel.length ? `Start ${sel.length + 1} ture` : others.length ? "Start på hængeren" : "Start kun denne tur");
  const name = (t) => `${t.fraction || "Tur"}${containerNo(t) ? ` (${containerNo(t)})` : ""}${capacityShort(t) ? ` · ${capacityShort(t)}` : ""}`;

  return createPortal(
    <div className="fixed inset-0 z-[85] flex items-end sm:items-center justify-center" data-testid="run-sheet">
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => !busy && onClose()} />
      <div className="relative w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl max-h-[88vh] flex flex-col"
        style={{ paddingBottom: "max(12px, env(safe-area-inset-bottom))" }}>
        <div className="mx-auto mt-2 w-10 h-1.5 rounded-full bg-slate-200 sm:hidden" />
        <div className="px-5 pt-3 pb-2 flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-extrabold text-slate-900">{addMode ? "Tilføj tur på hængeren" : "Kører du flere containere?"}</h3>
            <p className="text-[13px] text-slate-500">
              {basePlads || anchor.plads || "Pladsen"}{trailerName ? ` · hænger ${trailerName}` : ""} · {slots > 0 ? `plads til ${slots} mere` : "hængeren er fuld"}
            </p>
          </div>
          <button onClick={() => !busy && onClose()} className="p-2 -mr-2 text-slate-500" aria-label="Luk"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 overflow-y-auto space-y-3">
          <div className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2 text-[13px]" data-testid="run-sheet-current">
            {(addMode ? active : [...others, anchor]).map((t, i) => (
              <div key={t.id} className="flex items-center gap-2 py-0.5">
                <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-bold ${i === 0 ? "bg-slate-900 text-white" : "bg-violet-600 text-white"}`}>{i === 0 ? "Bil" : "Hænger"}</span>
                <span className="font-semibold text-slate-800 truncate">{name(t)}</span>
                {t.id === anchor.id && !addMode && <span className="text-[11px] text-slate-500 shrink-0">starter nu</span>}
                {/* 2026-10-25: Hvornår og for hvem - en glemt tur (fx for en anden kunde) skal kunne genkendes. */}
                {t.id !== anchor.id && (t.on_way_time || t.other_customer) && (
                  <span className="text-[11px] text-slate-500 shrink-0">{t.on_way_time ? `startet ${t.on_way_time}` : ""}{t.other_customer ? `${t.on_way_time ? " · " : ""}${t.other_customer}` : ""}</span>
                )}
              </div>
            ))}
            {!addMode && others.length > 0 && (
              <p className="mt-1 text-[12px] text-slate-500">Du kører allerede {others.length === 1 ? "en tur" : `${others.length} ture`}. Er den færdig, så afslut den først.</p>
            )}
          </div>
          {slots > 0 && groups.length === 0 && (
            <div className="rounded-xl border border-dashed border-slate-300 px-3 py-3 text-sm text-slate-600" data-testid="run-sheet-empty">
              Ingen andre ture på {basePlads || anchor.plads || "pladsen"} lige nu.
              <span className="block text-[12px] text-slate-500 mt-0.5">Bliver en container mere klar, kan du lægge den på hængeren inde i turen ("Tilføj tur på hængeren").</span>
            </div>
          )}
          {slots > 0 && groups.map(g => (
            <section key={g.key} className="space-y-1.5">
              <div className="flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-wide text-slate-500">
                {g.title}
              </div>
              <div className="rounded-xl border border-slate-200 divide-y divide-slate-100 overflow-hidden">
                {g.list.map(t => {
                  const on = sel.includes(t.id);
                  const full = !on && sel.length >= slots;
                  const oh = openHours(t);
                  return (
                    <button key={t.id} type="button" onClick={() => toggle(t.id)} disabled={full} data-testid={`run-candidate-${t.id}`}
                      className={`w-full text-left px-3 py-2.5 flex items-center gap-3 ${on ? "bg-violet-50" : "bg-white"} ${full ? "opacity-40" : "active:bg-slate-50"}`}>
                      <span className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center ${on ? "bg-violet-600 border-violet-600 text-white" : "border-slate-300"}`}>
                        {on && <Check className="w-4 h-4" />}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-bold text-[14px] text-slate-900 truncate">{name(t)}</span>
                        <span className="flex items-center gap-1.5 text-[12px] text-slate-500 flex-wrap">
                          {isHaster(t) && <span className="inline-flex items-center gap-0.5 font-bold text-rose-600"><Flag className="w-3 h-3" />Haster</span>}
                          {oh.from && <span>{oh.from}–{oh.to}</span>}
                          {daysLate(t) > 0 && (
                            <span className="inline-flex items-center gap-0.5 px-1.5 rounded bg-amber-100 text-amber-800 font-bold" data-testid={`run-candidate-late-${t.id}`}>
                              <Clock className="w-3 h-3" />{new Date(`${String(t.date).slice(0, 10)}T12:00:00`).toLocaleDateString("da-DK", { day: "numeric", month: "numeric" })} · {daysLate(t)} {daysLate(t) === 1 ? "dag" : "dage"} for sent
                            </span>
                          )}
                          <span className={`px-1.5 rounded ${t._mine ? "bg-indigo-50 text-indigo-700" : "bg-orange-50 text-orange-700"} font-bold`}>{t._mine ? "Din tur" : "Ledig"}</span>
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
          {slots > 0 && elsewhereCount > 0 && (
            <section className="space-y-1.5" data-testid="run-sheet-elsewhere">
              <button type="button" onClick={() => setShowOther(v => !v)} className="w-full flex items-center gap-1.5 px-1 text-[11px] font-bold uppercase tracking-wide text-slate-500" data-testid="run-sheet-elsewhere-toggle">
                <MapPin className="w-3.5 h-3.5" /> Andre pladser ({elsewhereCount})
                <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${showOther || pickedElsewhere.length ? "rotate-180" : ""}`} />
              </button>
              {(showOther || pickedElsewhere.length > 0) && elsewhere.map(g => (
                <div key={g.key} className="rounded-xl border border-slate-200 overflow-hidden">
                  <div className="px-3 py-1.5 bg-slate-50 text-[12px] font-bold text-slate-700 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-slate-400" />{g.key}</div>
                  <div className="divide-y divide-slate-100">
                    {g.list.map(t => {
                      const on = sel.includes(t.id);
                      const full = !on && sel.length >= slots;
                      return (
                        <button key={t.id} type="button" onClick={() => toggle(t.id)} disabled={full} data-testid={`run-candidate-${t.id}`}
                          className={`w-full text-left px-3 py-2.5 flex items-center gap-3 ${on ? "bg-violet-50" : "bg-white"} ${full ? "opacity-40" : "active:bg-slate-50"}`}>
                          <span className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center ${on ? "bg-violet-600 border-violet-600 text-white" : "border-slate-300"}`}>
                            {on && <Check className="w-4 h-4" />}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block font-bold text-[14px] text-slate-900 truncate">{name(t)}</span>
                            <span className="flex items-center gap-1.5 text-[12px] text-slate-500 flex-wrap">
                              {isHaster(t) && <span className="inline-flex items-center gap-0.5 font-bold text-rose-600"><Flag className="w-3 h-3" />Haster</span>}
                              {daysLate(t) > 0 && <span className="px-1.5 rounded bg-amber-100 text-amber-800 font-bold">{daysLate(t)} {daysLate(t) === 1 ? "dag" : "dage"} for sent</span>}
                              <span className={`px-1.5 rounded ${t._mine ? "bg-indigo-50 text-indigo-700" : "bg-orange-50 text-orange-700"} font-bold`}>{t._mine ? "Din tur" : "Ledig"}</span>
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </section>
          )}
          {pickedElsewhere.length > 0 && (
            <div className="rounded-xl bg-sky-50 border border-sky-200 px-3 py-2 text-[13px] text-sky-900" data-testid="run-sheet-elsewhere-note">
              Hentes på {[...new Set(pickedElsewhere.map(t => t.plads || "en anden plads"))].join(" og ")} - et andet sted end {basePlads || "bilens container"}.
            </div>
          )}
        </div>
        <div className="px-4 pt-3 flex gap-2">
          <button type="button" onClick={onClose} disabled={busy} className="h-12 px-4 rounded-xl border border-slate-200 text-slate-700 font-bold">Annuller</button>
          <button type="button" onClick={confirm} disabled={busy || (addMode && !sel.length)} data-testid="run-sheet-confirm"
            className="flex-1 h-12 rounded-xl bg-orange-500 active:bg-orange-600 disabled:opacity-50 text-white font-extrabold inline-flex items-center justify-center gap-2">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} {label}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
